import styles from "./index.module.css";
import { UserOutlined } from "@ant-design/icons";
import sealWarningWithoutBackground from "assets/svg/SealWarningWithoutBackground.svg";

import { Avatar, Divider, Spin, Tooltip } from "antd";
import { approveVerification, rejectVerification, UserDetails } from "helpers/backend_helper";
import { SocketContext } from "context/socketContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
// import { API_ORIGIN } from "helpers/constants";
import { useDebounce, useRequest } from "ahooks";
import { NotificationContext } from "context/notificationContext";
import { VerificationModal } from "../VerificationModal";
import { CustomButton } from "Components/CustomButton";
import sealCheckBlackAndWhite from "assets/svg/SealCheckBlackAndWhite.svg";
import sealCheck from "assets/svg/SealCheck.svg";
import clockCountdown from "assets/svg/ClockCountdown.svg";
import phone from "assets/svg/Phone.svg";
import envelopeSimple from "assets/svg/EnvelopeSimple.svg";
import clockGray from "assets/svg/ClockCountdownGray.svg";
import mapPin from "assets/svg/MapPin.svg";
import lockRed from "assets/svg/LockRed.svg";
import lockOpenGray from "assets/svg/LockOpenGray.svg";
import { removePostalIndexCountryCity } from "helpers/helper";
import { formatLastOnline, formatShortDate } from "helpers/format";
import { AdminContext } from "context/adminContext";
import { getAvatarSrc } from "Components/UserNameRatingPhotoComponent/UserNameRatingPhotoComponent";

type Props = {
  userInfo: UserDetails;
  toFrozeUser: (isFrozen: boolean) => void;
  countryUserId: string;
  onSize: (s: number) => void;
  isRefresUserDetailsLoading: boolean;
  refreshVerificationVersions: () => void;
  verificationVersions: any;
  refreshUserDetails: () => void;
  verificationUserDetails: any;
  setVerificationUserDetails: any;
};

export const MainInfo = ({
  userInfo,
  toFrozeUser,
  countryUserId,
  onSize,
  isRefresUserDetailsLoading,
  refreshVerificationVersions,
  refreshUserDetails,
  verificationVersions,
  verificationUserDetails,
  setVerificationUserDetails,
}: Props) => {
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const { localCountry } = useContext(AdminContext);
  const lastOnlineText = useMemo(() => formatLastOnline(userInfo.lastOnlineDate), [userInfo.lastOnlineDate]);

  const debouncedLoadingVerifDetails = useDebounce(isRefresUserDetailsLoading, { wait: 100 });

  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const onCloseVerificationModal = () => {
    setIsVerificationModalOpen(false);
  };
  const onOpenVerificationModal = () => {
    setIsVerificationModalOpen(true);
  };

  useEffect(() => {
    if (userInfo) {
      onSize(ref.current.clientHeight);
    }
  }, [userInfo, verificationUserDetails, debouncedLoadingVerifDetails]);

  const ref = useRef<HTMLDivElement>();
  const { online } = useContext(SocketContext);

  const { runAsync: rejectUserVerification, loading: isRejectUserVerificationLoading } = useRequest(
    () => rejectVerification(verificationUserDetails.userId, "", localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!verificationUserDetails?.userId && !!localCountry.iso,
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: ({ success, message, user }) => {
        // eslint-disable-next-line no-debugger
        debugger;
        if (success) {
          openNotificationWithIcon("success", message);
          setVerificationUserDetails((prev) => ({
            ...prev,
            clientType: user.clientType,
            businessInfo: user.businessInfo,
            verificationStatus: user.verificationStatus,
          }));
          refreshVerificationVersions();
        } else {
          openNotificationWithIcon("info", message);
        }
      },
    },
  );

  const { runAsync: approveUserVerification, loading: isApproveUserVerificationLoading } = useRequest(
    () => approveVerification(verificationUserDetails.userId, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!verificationUserDetails?.userId && !!localCountry?.iso,
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: ({ success, message }) => {
        if (success) {
          openNotificationWithIcon("success", message);
          refreshUserDetails();
        } else {
          openNotificationWithIcon("info", message);
        }
      },
    },
  );

  const isTimeToShowSeal = verificationUserDetails?.expiresAt
    ? new Date(verificationUserDetails.expiresAt).getMonth() === new Date().getMonth()
    : false;

  const companyName =
    verificationUserDetails?.clientType === "BUSINESS" && verificationUserDetails?.businessInfo?.companyName
      ? verificationUserDetails?.businessInfo?.companyName
      : null;
  const clientTypeInfo =
    verificationUserDetails?.clientType === "PERSON"
      ? "Person"
      : verificationUserDetails?.clientType === "ENTREPRENEUR"
      ? "Entrepreneur"
      : verificationUserDetails?.clientType === "BUSINESS"
      ? companyName
      : null;

  const formattedAddress = removePostalIndexCountryCity(
    verificationUserDetails?.residentialAddress ?? userInfo.address,
  );

  const showNew =
    verificationUserDetails &&
    verificationUserDetails?.verificationStatus !== "EXPIRED" &&
    verificationUserDetails?.verificationStatus !== "REJECTED" &&
    verificationUserDetails?.verificationStatus !== "UNVERIFIED" &&
    verificationUserDetails.kycProfileStatus !== "EXPIRED";

  return (
    <div className={styles.container} ref={ref}>
      {debouncedLoadingVerifDetails ? (
        <Spin style={{ top: "50%", left: "50%" }} />
      ) : (
        <>
          <div className={styles.mainInfoWithIcon}>
            {userInfo.avatar ? (
              <Avatar
                icon={<img src={getAvatarSrc(userInfo.avatar)} alt="" />}
                // icon={<img src={API_ORIGIN + "/public/avatars/" + userInfo.avatar} alt="" />}
                size={92}
                className={styles.avatar}
              />
            ) : (
              <Avatar icon={<UserOutlined />} size={92} />
            )}
            <div className={styles.mainInfoWrapperText}>
              <div className={styles.displayFlex} style={{ justifyContent: "space-between", width: "100%" }}>
                <div>
                  <div className={styles.userName}>
                    {showNew ? verificationUserDetails?.firstName : userInfo.firstName}{" "}
                    {showNew ? verificationUserDetails?.lastName : userInfo.lastName}
                  </div>
                  <div style={{ fontWeight: 400, color: "#868686", fontSize: 14 }}>
                    {userInfo.online || (online.clientId === countryUserId && online.online) ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        online <span className={styles.onlineDot}></span>
                      </div>
                    ) : userInfo?.lastOnlineDate ? (
                      online.clientId === countryUserId && !online.online ? (
                        formatLastOnline(new Date())
                      ) : (
                        lastOnlineText
                      )
                    ) : null}
                  </div>
                </div>
                {verificationUserDetails?.veriffData ? (
                  <CustomButton sizeButton="medium" typeButton="tertiary" onClick={onOpenVerificationModal}>
                    <div className={styles.displayFlex} style={{ color: "#000", gap: 4 }}>
                      <img src={sealCheckBlackAndWhite} alt="seal check" width={16} height={16} />
                      <div>KYC</div>
                    </div>
                  </CustomButton>
                ) : null}
              </div>
              <div className={styles.userInfo}>
                <div>
                  <img src={phone} alt="phone" />
                  <span>{userInfo.phone}</span>
                </div>
                <div>
                  <img src={envelopeSimple} alt="envelope" />
                  <span>{userInfo.email}</span>
                </div>
                <div>
                  <img src={mapPin} alt="map pin" />
                  <span>
                    {
                      <Tooltip title={formattedAddress.length > 39 ? formattedAddress : null}>
                        {formattedAddress.slice(0, 39) + "..."}
                      </Tooltip>
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.verifieWrapper}>
            <div>
              {verificationUserDetails?.verificationStatus === "UNVERIFIED" ||
              !verificationUserDetails ||
              verificationUserDetails?.verificationStatus === "REJECTED" ? (
                <>
                  <img src={sealWarningWithoutBackground} alt="warning" /> <div>Not verified</div>
                </>
              ) : null}

              {verificationUserDetails?.verificationStatus === "KYC_COMPLETED" ||
              verificationUserDetails?.verificationStatus === "KYC_PENDING" ||
              verificationUserDetails?.verificationStatus === "EXPIRED" ? (
                <>
                  <img src={clockCountdown} alt="warning" />
                  <div style={{ minWidth: 130, lineHeight: "100%" }}>
                    Under Review <br />
                    <span style={{ color: "#888888", fontWeight: 500 }}>as {clientTypeInfo}</span>
                  </div>
                </>
              ) : null}

              {verificationUserDetails?.verificationStatus === "VERIFIED" ? (
                <>
                  <img src={sealCheck} alt="warning" />{" "}
                  <div style={{ lineHeight: "100%" }}>
                    Verified <br />
                    <span style={{ color: "#888888", fontWeight: 500 }}>as {clientTypeInfo}</span>
                  </div>
                  {isTimeToShowSeal ? (
                    <Tooltip title={formatShortDate(verificationUserDetails.expiresAt)}>
                      <img src={clockGray} alt="clockGray" />
                    </Tooltip>
                  ) : null}
                </>
              ) : null}
            </div>
            <div>
              Joined {new Date(userInfo.createdAt).toLocaleDateString("en-EN", { year: "numeric", month: "short" })}
            </div>
          </div>

          {verificationUserDetails &&
          verificationUserDetails?.verificationStatus !== "UNVERIFIED" &&
          verificationUserDetails?.verificationStatus !== "REJECTED" &&
          verificationUserDetails?.verificationStatus !== "VERIFIED" ? (
            <div className={styles.displayFlex} style={{ gap: 6, marginTop: 12 }}>
              <CustomButton
                sizeButton="medium"
                typeButton="critical"
                style={{ width: "100%" }}
                onClick={rejectUserVerification}
                loading={isRejectUserVerificationLoading || isApproveUserVerificationLoading}
              >
                <div>Decline</div>
              </CustomButton>
              <Tooltip
                title={
                  verificationUserDetails?.verificationStatus !== "KYC_COMPLETED" ? "Waiting for ID verification" : null
                }
              >
                <CustomButton
                  sizeButton="medium"
                  typeButton="primary"
                  style={{ width: "100%" }}
                  disabled={verificationUserDetails?.verificationStatus !== "KYC_COMPLETED"}
                  onClick={approveUserVerification}
                  loading={isRejectUserVerificationLoading || isApproveUserVerificationLoading}
                >
                  <div>Accept</div>
                </CustomButton>
              </Tooltip>
            </div>
          ) : null}
          <CustomButton
            sizeButton="medium"
            typeButton={userInfo.frozen ? "tertiary" : "critical"}
            onClick={() => {
              toFrozeUser(!userInfo.frozen);
            }}
            style={{ width: "100%", marginTop: 18, marginBottom: 24 }}
          >
            <div className={styles.displayFlex} style={{ gap: "4px" }}>
              {userInfo.frozen ? (
                <img src={lockOpenGray} alt="lock gray" width={16} height={16} />
              ) : (
                <img src={lockRed} alt="locke" width={16} height={16} />
              )}
              <div>{userInfo.frozen ? "Unblock User" : "Block User"}</div>
            </div>
          </CustomButton>
          <Divider style={{ borderWidth: 3 }} />
          <p className={styles.description}>{userInfo.description}</p>
          <VerificationModal
            countryUserId={countryUserId}
            isOpen={isVerificationModalOpen}
            onCloseModal={onCloseVerificationModal}
            userInfo={userInfo}
            verificationVersions={verificationVersions?.versions}
          />
        </>
      )}
    </div>
  );
};
